/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { useTranslation } from 'react-i18next';
import { useSite } from '@salesforce/storefront-next-runtime/site-context';

import { Button } from '@/components/ui/button';
import { FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { FormInput } from '@/components/form-fields';

import { FETCHER_STATES } from '@/lib/fetcher-states';
import { getCurrencySymbol } from '@/lib/currency';
import { type DonationFieldsProps } from './types';

/**
 * DonationFields — the widget's field row, matching `PromoCodeFields`:
 * currency-prefixed amount field + a confirm button whose label switches
 * between "Add Donation" / "Edit Donation" depending on whether a donation
 * is already applied. Input and button are both disabled while a mutation
 * is in flight.
 */
export function DonationFields({ form, fetcher, isEditMode }: DonationFieldsProps) {
    const { t, i18n } = useTranslation('cart');
    const { currency } = useSite();
    const currencySymbol = getCurrencySymbol(i18n.language, currency);
    const isSubmitting = fetcher.state === FETCHER_STATES.SUBMITTING;

    return (
        <div className="space-y-2">
            <FormField
                control={form.control}
                name="amount"
                render={({ field }) => (
                    <FormItem>
                        <FormLabel className="sr-only">{t('donation.label')}</FormLabel>
                        <div className="flex gap-2">
                            <div className="rounded-none border-solid border-[var(--input)] border py-2 px-3 flex flex-row gap-2 items-center justify-start flex-1 relative overflow-hidden shadow-xs">
                                <span
                                    className="shrink-0 flex items-center justify-center text-sm text-muted-foreground"
                                    aria-hidden="true">
                                    {currencySymbol}
                                </span>
                                <FormInput
                                    type="number"
                                    inputMode="decimal"
                                    min="0"
                                    step="0.01"
                                    placeholder={t('donation.placeholder')}
                                    disabled={isSubmitting}
                                    className="border-0 p-0 h-auto text-sm bg-transparent focus-visible:ring-0 focus-visible:ring-offset-0 placeholder:text-muted-foreground w-full"
                                    {...field}
                                />
                            </div>
                            <Button
                                type="submit"
                                variant="secondary"
                                disabled={isSubmitting}
                                className="cursor-pointer px-4">
                                {isSubmitting
                                    ? t('donation.saving')
                                    : isEditMode
                                      ? t('donation.editButton')
                                      : t('donation.addButton')}
                            </Button>
                        </div>
                        <FormMessage />
                    </FormItem>
                )}
            />
        </div>
    );
}
